import { clearDiscussionCaptures } from '../canntalk/discussionCapture';
import {
  AssessmentResponseSchema,
  BootstrapSchema,
  ChatResultSchema,
  ConversationReportSchema,
  TranslationAnswerSchema,
  type AssessmentResponse,
  type Bootstrap,
  type ChatResult,
  type ChatScenario,
  type ChatTranscriptEntry,
  type ConversationAssessment,
  type ConversationReport,
  type RealtimeChatStatusState,
  type TranslationAnswer,
} from '../../shared/contracts';
import { recordingToWavFile } from './wav';

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = init?.body instanceof FormData
    ? init.headers
    : init?.body !== undefined
      ? { 'Content-Type': 'application/json', ...init?.headers }
      : init?.headers;
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers,
  });
  if (!response.ok) {
    if (response.status === 401 && path !== '/api/auth/access') window.dispatchEvent(new Event('canntalk-auth-expired'));
    const body = await response.json().catch(() => ({ error: 'Something went wrong.' }));
    throw new ApiError(body.error ?? 'Something went wrong.', response.status);
  }
  return response.json() as Promise<T>;
}

export async function getBootstrap(): Promise<Bootstrap> {
  return BootstrapSchema.parse(await request('/api/bootstrap'));
}

export async function unlock(code: string) {
  return request<{ ok: true }>('/api/auth/access', { method: 'POST', body: JSON.stringify({ code }) });
}

export async function logout() {
  clearDiscussionCaptures();
  return request<{ ok: true }>('/api/auth/logout', { method: 'POST' });
}

export async function resetDemo() {
  return request<{ ok: true }>('/api/demo/reset', { method: 'POST' });
}

export async function getSpeechToken() {
  return request<{ mode: 'live' | 'demo'; region: string; token?: string }>('/api/speech/token', { method: 'POST' });
}

export async function translatePhrase(text: string, hint?: 'english' | 'mandarin'): Promise<TranslationAnswer> {
  return TranslationAnswerSchema.parse(await request('/api/translate', {
    method: 'POST',
    body: JSON.stringify(hint ? { text, hint } : { text }),
  }));
}

export async function createConversation() {
  return request<{ id: string; turn: { id: string; role: 'waiter'; cantonese: string; jyutping?: string; english?: string } }>('/api/conversations', { method: 'POST' });
}

export async function sendConversationTurn(id: string, transcript: string) {
  return request<{ learner: { id: string; role: 'learner'; cantonese: string }; waiter: { id: string; role: 'waiter'; cantonese: string; jyutping?: string; english?: string }; mode: 'live' | 'demo' }>(`/api/conversations/${id}/turns`, {
    method: 'POST', body: JSON.stringify({ transcript }),
  });
}

export async function finishConversation(id: string): Promise<ConversationReport> {
  return ConversationReportSchema.parse(await request(`/api/conversations/${id}/complete`, { method: 'POST' }));
}

export async function getChatScenarios(): Promise<ChatScenario[]> {
  const body = await request<{ scenarios: ChatScenario[] }>('/api/chat/scenarios');
  return body.scenarios;
}

export interface RealtimeChatHandlers {
  onReady?: (scenario: ChatScenario) => void;
  onTranscript?: (entry: ChatTranscriptEntry) => void;
  onStatus?: (state: RealtimeChatStatusState, tool?: string) => void;
  onObjective?: (completedSteps: string[], currentStep: string | null) => void;
  onAudio?: (pcm: ArrayBuffer) => void;
  onAllObjectivesComplete?: () => void;
  onAssessingConversation?: () => void;
  onAssessmentComplete?: (assessment: ConversationAssessment) => void;
  onResult?: (result: ChatResult) => void;
  onError?: (message: string) => void;
  onClose?: () => void;
  onLanguageWarning?: (messageZh: string, messageEn: string, confidence: number) => void;
}

export interface RealtimeChatController {
  ready: (timeoutMs?: number) => Promise<void>;
  start: (scenarioId: string) => void;
  sendAudio: (pcm: ArrayBuffer) => void;
  giveUp: () => void;
  close: () => void;
}

/** WebSocket client for the realtime voice conversation (Qwen-Omni-Realtime bridge). */
export function openRealtimeChatSocket(handlers: RealtimeChatHandlers): RealtimeChatController {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const ws = new WebSocket(`${protocol}//${window.location.host}/api/chat/realtime`);
  ws.binaryType = 'arraybuffer';

  ws.addEventListener('message', (event: MessageEvent) => {
    if (typeof event.data !== 'string') {
      handlers.onAudio?.(event.data as ArrayBuffer);
      return;
    }
    let message: Record<string, unknown>;
    try {
      message = JSON.parse(event.data) as Record<string, unknown>;
    } catch {
      return;
    }
    switch (message.type) {
      case 'ready':
        handlers.onReady?.(message.scenario as ChatScenario);
        break;
      case 'transcript':
        handlers.onTranscript?.({ role: message.role as 'waiter' | 'learner', text: String(message.text ?? '') });
        break;
      case 'status':
        handlers.onStatus?.(message.state as RealtimeChatStatusState, typeof message.tool === 'string' ? message.tool : undefined);
        break;
      case 'objective_update':
        handlers.onObjective?.(
          Array.isArray(message.completedSteps) ? message.completedSteps as string[] : [],
          typeof message.currentStep === 'string' ? message.currentStep : null,
        );
        break;
      case 'language_warning':
        handlers.onLanguageWarning?.(
          String(message.messageZh ?? ''),
          String(message.messageEn ?? ''),
          typeof message.confidence === 'number' ? message.confidence : 0,
        );
        break;
      case 'all_objectives_complete':
        handlers.onAllObjectivesComplete?.();
        break;
      case 'assessing_conversation':
        handlers.onAssessingConversation?.();
        break;
      case 'assessment_complete':
        handlers.onAssessmentComplete?.(message.assessment as ConversationAssessment);
        break;
      case 'result':
        handlers.onResult?.(ChatResultSchema.parse(message.result));
        break;
      case 'error':
        handlers.onError?.(String(message.message ?? 'Realtime chat error.'));
        break;
    }
  });
  ws.addEventListener('close', () => handlers.onClose?.());
  ws.addEventListener('error', () => handlers.onError?.('WebSocket connection error.'));

  return {
    ready: (timeoutMs = 10_000) => new Promise<void>((resolve, reject) => {
      if (ws.readyState === WebSocket.OPEN) return resolve();
      const timer = window.setTimeout(() => reject(new Error('WebSocket connection timed out.')), timeoutMs);
      ws.addEventListener('open', () => {
        window.clearTimeout(timer);
        resolve();
      }, { once: true });
      ws.addEventListener('error', () => {
        window.clearTimeout(timer);
        reject(new Error('WebSocket connection failed.'));
      }, { once: true });
    }),
    start(scenarioId: string) {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'start', scenarioId }));
    },
    sendAudio(pcm: ArrayBuffer) {
      if (ws.readyState === WebSocket.OPEN) ws.send(pcm);
    },
    giveUp() {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'give_up' }));
    },
    close() {
      try {
        ws.close();
      } catch {
        /* already closed */
      }
    },
  };
}

export async function uploadRecording(blob: Blob) {
  const form = new FormData();
  const type = blob.type.split(';')[0].toLowerCase();
  const extension = type === 'audio/mp4' ? 'm4a' : type === 'audio/ogg' ? 'ogg' : type === 'audio/mpeg' ? 'mp3' : type === 'audio/wav' ? 'wav' : 'webm';
  form.append('audio', blob, `utterance.${extension}`);
  return request<{ id: string; mode: 'live' | 'demo'; expiresAt: string }>('/api/recordings', { method: 'POST', body: form });
}

export async function assessRecording(recording: Blob, jyutping: string, text: string): Promise<AssessmentResponse> {
  const form = new FormData();
  form.append('jyutping', jyutping);
  form.append('text', text);
  form.append('audio', await recordingToWavFile(recording), 'utterance.wav');
  const rawResponse = await request('/api/assessments', { method: 'POST', body: form });
  console.log('[assessRecording] Raw response:', rawResponse);
  console.log('[assessRecording] Has initial_final:', 'initial_final' in ((rawResponse as any)?.result?.segmental?.details || {}));
  const parsed = AssessmentResponseSchema.parse(rawResponse);
  console.log('[assessRecording] Parsed response:', parsed);
  console.log('[assessRecording] After parse - Has initial_final:', 'initial_final' in (parsed.result.segmental.details || {}));
  return parsed;
}

export async function fetchReferenceAudio(text: string): Promise<{ blob: Blob; generator: string }> {
  const response = await fetch(`/api/reference-audio?text=${encodeURIComponent(text)}`, { credentials: 'include' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Reference audio unavailable.' }));
    throw new ApiError(body.error ?? 'Reference audio unavailable.', response.status);
  }
  return { blob: await response.blob(), generator: response.headers.get('x-reference-generator') ?? '' };
}
